import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import AuthService from "@/integrations/service/auth.service";
import { adminLogout } from "@/pages/admin/RequireAdmin";

const WARNING_WINDOW_MS = 5 * 60 * 1000; // 5 minutes avant expiration
const POLL_INTERVAL_MS = 1000; // lecture localStorage uniquement — coût négligeable, permet un compte à rebours fluide

function fmt(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function SessionTimeoutWatcher() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const [extending, setExtending] = useState(false);
  const loggedOutRef = useRef(false);

  useEffect(() => {
    const tick = () => {
      const expiresAtRaw = localStorage.getItem("tokenExpiresAt");
      if (!expiresAtRaw) {
        setRemainingMs(null);
        return;
      }
      const expiresAt = Number(expiresAtRaw);
      const remaining = expiresAt - Date.now();

      if (remaining <= 0) {
        if (!loggedOutRef.current) {
          loggedOutRef.current = true;
          adminLogout();
          navigate("/redaction/login", { replace: true });
        }
        return;
      }

      setRemainingMs(remaining <= WARNING_WINDOW_MS ? remaining : null);
    };

    tick();
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [navigate]);

  const handleExtend = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) return;
    setExtending(true);
    try {
      await AuthService.refresh(refreshToken);
      setRemainingMs(null);
    } catch {
      adminLogout();
      navigate("/redaction/login", { replace: true });
    } finally {
      setExtending(false);
    }
  };

  if (remainingMs === null) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-lg border border-border bg-background shadow-2xl p-6 space-y-4">
        <div className="flex items-center gap-2 text-alert">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <h2 className="font-serif text-base font-semibold text-foreground">
            {t("admin.session.expiringSoon.title")}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground">
          {t("admin.session.expiringSoon.desc")}{" "}
          <span className="font-mono font-semibold text-foreground">{fmt(remainingMs)}</span>
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={() => {
              adminLogout();
              navigate("/redaction/login", { replace: true });
            }}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
          >
            {t("admin.session.logoutNow")}
          </button>
          <button
            onClick={handleExtend}
            disabled={extending}
            className="flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors"
          >
            {extending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t("admin.session.extend")}
          </button>
        </div>
      </div>
    </div>
  );
}
